import Vue from 'vue';
import moment from 'moment';
import kidSchoolApi from '@/api/kidSchoolApi';
import { enqueue, shouldQueue } from '@/services/KidSchoolQueue';
import { logActivity } from '@/services/ActivityLogService';
import { callFn } from '@/utils/functionsClient';
const NAME = 'Dětská školka';
const FULL_REFUND_HOURS_BEFORE = 24;
function toKidSchool(resource) {
    return {
        id: resource.id,
        capacity: resource.capacity,
        finalKidCount: resource.capacity - resource.available_spots,
        dateTime: resource.starting_at,
        length: resource.duration_minutes,
        price: resource.price_czk,
        isPremium: resource.is_premium,
        instructorsCount: resource.instructors_count,
        isPrepaidOnly: resource.is_prepaid_only,
    };
}
function toKid(resource) {
    return {
        kidId: resource.kid_id ?? '',
        reservationId: resource.reservation_id ?? '',
        name: `${resource.first_name} ${resource.last_name}`,
        age: moment().diff(moment({ year: resource.birth_year, month: resource.birth_month - 1 }), 'years'),
        note: resource.notes ?? '',
        parentName: resource.parent_name ?? '',
        parentPhone: resource.parent_phone ?? '',
        price: resource.price_czk ?? 0,
        isPaid: resource.paid ?? resource.is_paid ?? false,
        receiptId: resource.receipt_id,
        attendance: resource.attendance ?? null,
        paymentMethod: resource.payment_method ?? '',
        visitsCount: resource.visits_count ?? 0,
        lastAttendedAt: resource.last_attended_at ?? '',
    };
}
/**
 * The note as the screen shows it until the queue delivers it. The author is
 * unknown here, so the card marks it as waiting instead of offering the edit.
 */
function queuedNote({ kidId, kidSchoolId, content, noteId, lessonId }) {
    return {
        id: noteId ?? `pending-${Date.now()}`,
        kidId,
        lessonId: noteId ? lessonId ?? '' : kidSchoolId,
        authorId: '',
        content,
        authorName: '',
        createdAt: new Date().toISOString(),
        pending: true,
    };
}
function toWritePayload(kidSchool) {
    return {
        capacity: kidSchool.capacity,
        duration_minutes: kidSchool.length,
        price_czk: kidSchool.price,
        is_premium: kidSchool.isPremium ?? false,
        is_prepaid_only: kidSchool.isPrepaidOnly,
        instructors_count: kidSchool.instructorsCount,
        full_refund_hours_before: FULL_REFUND_HOURS_BEFORE,
    };
}
/** How far back the card reads the child's notes. */
const NOTE_PAGE_SIZE = 20;
function toKidNote(resource) {
    return {
        id: resource.id,
        kidId: resource.kid_id,
        lessonId: resource.lesson_id ?? '',
        authorId: resource.author_id,
        content: resource.content,
        authorName: resource.author_name ?? '',
        createdAt: resource.created_at,
    };
}
async function fetchById(id) {
    return toKidSchool((await kidSchoolApi.get(`/api/v1/internal/kid-schools/${id}`)).data);
}
export const KidSchoolModule = {
    namespaced: true,
    state: [],
    mutations: {
        setAll(state, kidSchools) {
            state.splice(0, state.length);
            state.push(...kidSchools);
        },
        store(state, kidSchool) {
            state.push(kidSchool);
        },
        update(state, kidSchool) {
            state.find((oldKidSchool, index) => {
                if (oldKidSchool.id === kidSchool.id) {
                    Vue.set(state, index, { ...kidSchool, kids: oldKidSchool.kids });
                }
            });
        },
        setKidPayment(state, { id, kidId, receiptId }) {
            const kid = state.find(x => x.id === id)?.kids.find(x => x.kidId === kidId);
            if (kid) {
                Vue.set(kid, 'isPaid', true);
                Vue.set(kid, 'receiptId', receiptId);
            }
        },
        setKidAttendance(state, { id, kidId, attendance }) {
            const kid = state.find(x => x.id === id)?.kids.find(x => x.kidId === kidId);
            if (kid) {
                Vue.set(kid, 'attendance', attendance);
            }
        },
        setKidReceipt(state, { id, kidId, receiptId, receiptPdfPath }) {
            const kid = state.find(x => x.id === id)?.kids.find(x => x.kidId === kidId);
            if (kid) {
                Vue.set(kid, 'receiptId', receiptId);
                Vue.set(kid, 'receiptPdfPath', receiptPdfPath ?? '');
            }
        },
        setKids(state, { id, kids }) {
            const kidSchool = state.find(x => x.id === id);
            if (kidSchool) {
                Vue.set(kidSchool, 'kids', kids);
            }
        },
        destroy(state, id) {
            state.splice(state.findIndex(item => id === item.id), 1);
        },
        clear(state) {
            state.splice(0, state.length);
        },
    },
    actions: {
        async getAll(context) {
            try {
                const request = (await kidSchoolApi.get(`/api/v1/internal/kid-schools?center_id=${process.env.VUE_APP_AREAL_ID}`));
                context.commit('setAll', request.data.data.map((resource) => ({ ...toKidSchool(resource), kids: [] })));
                // The schedule does not wait for this list, so days already on screen
                // have to pick the lessons up.
                await context.dispatch('instructorTable/refreshKidSchools', null, { root: true });
            }
            catch (e) {
                console.log(e);
                throw e;
            }
        },
        async getById(context, id) {
            try {
                const kidSchool = await fetchById(id);
                context.commit('update', kidSchool);
                return context.state.find((x) => x.id === id);
            }
            catch (e) {
                console.log(e);
                context.commit('destroy', id);
                throw (e);
            }
        },
        async getRoster(context, id) {
            const request = (await kidSchoolApi.get(`/api/v1/internal/kid-schools/${id}/kids`));
            context.commit('setKids', { id, kids: request.data.data.map(toKid) });
        },
        /**
         * Records one child's arrival. The endpoint writes the kids it is given, so a single
         * child is written without touching its siblings on the same reservation.
         */
        async markAttendance(context, { id, kid, attended }) {
            const url = `/api/v1/internal/reservations/${kid.reservationId}/attendance`;
            const body = { kids: [{ kid_id: kid.kidId, attended }] };
            try {
                await kidSchoolApi.put(url, body);
            }
            catch (e) {
                // A phone on the slope loses the signal; the arrival is written down
                // here and sent when the backoffice is reachable again.
                if (!shouldQueue(e)) {
                    throw e;
                }
                enqueue({ method: 'put', url, body });
            }
            context.commit('setKidAttendance', { id, kidId: kid.kidId, attendance: attended });
        },
        /**
         * Instructor notes live with the child, not with the lesson, so the next instructor
         * reads what every colleague before wrote - newest first, as the endpoint returns them.
         */
        async getKidNotes(context, kidId) {
            const request = await kidSchoolApi.get(`/api/v1/internal/kids/${kidId}/notes?page_size=${NOTE_PAGE_SIZE}`);
            return (request.data.data ?? []).map((note) => toKidNote(note));
        },
        /**
         * The lesson is recorded with the note, so it can be traced back to the day.
         * An existing note is rewritten; the endpoint lets only its author do that.
         */
        async saveKidNote(context, { kidId, kidSchoolId, content, noteId, lessonId }) {
            // The rewrite overwrites the lesson, so the note carries its own one back -
            // empty for a note that has none, which the endpoint leaves unset.
            const url = noteId
                ? `/api/v1/internal/kids/${kidId}/notes/${noteId}`
                : `/api/v1/internal/kids/${kidId}/notes`;
            const body = { content, lesson_id: noteId ? lessonId ?? '' : kidSchoolId };
            try {
                const request = noteId
                    ? await kidSchoolApi.put(url, body)
                    : await kidSchoolApi.post(url, body);
                return toKidNote(request.data.data);
            }
            catch (e) {
                if (!shouldQueue(e)) {
                    throw e;
                }
                enqueue({ method: noteId ? 'put' : 'post', url, body });
                return queuedNote({ kidId, kidSchoolId, content, noteId, lessonId });
            }
        },
        /** The endpoint leaves an instructor their own notes; a moderator removes any. */
        async deleteKidNote(context, { kidId, noteId }) {
            const url = `/api/v1/internal/kids/${kidId}/notes/${noteId}`;
            try {
                await kidSchoolApi.delete(url);
            }
            catch (e) {
                if (!shouldQueue(e)) {
                    throw e;
                }
                enqueue({ method: 'delete', url });
            }
        },
        /**
         * Mirrors the day's crew into the backoffice, which authorizes attendance by
         * assignment. The instructor is identified by e-mail, the only identity both
         * systems share.
         */
        async assignInstructor(context, { id, email }) {
            await kidSchoolApi.post(`/api/v1/internal/kid-schools/${id}/instructors`, { email });
        },
        async unassignInstructor(context, { id, email }) {
            await kidSchoolApi.delete(`/api/v1/internal/kid-schools/${id}/instructors/${encodeURIComponent(email)}`);
        },
        /**
         * Receipts of the day, so a payment taken by someone else - or before a reload -
         * can still be reprinted. The roster carries no receipt of its own.
         */
        async getReceipts(context, { id, date }) {
            const receipts = await callFn('listKidSchoolReceipts', {
                kidSchoolId: id,
                date,
            });
            const kids = context.state.find((x) => x.id === id)?.kids ?? [];
            kids.forEach((kid) => {
                // An online payment pays the whole reservation, so its receipt has no kid.
                const receipt = receipts.find((x) => x.kidId === kid.kidId)
                    ?? receipts.find((x) => !x.kidId && x.reservationId === kid.reservationId);
                if (receipt) {
                    context.commit('setKidReceipt', {
                        id,
                        kidId: kid.kidId,
                        receiptId: receipt.receiptId,
                        receiptPdfPath: receipt.pdfPath,
                    });
                }
            });
        },
        /** The receipt is issued by `payKidSchoolKid`; the roster only catches up on its next load. */
        async markKidPaid(context, data) {
            context.commit('setKidPayment', data);
        },
        async store(context, kidSchool) {
            try {
                const request = (await kidSchoolApi.post('/api/v1/internal/kid-schools', {
                    ...toWritePayload(kidSchool),
                    center_id: process.env.VUE_APP_AREAL_ID,
                    name: NAME,
                    starting_at: moment(kidSchool.dateTime).toISOString(),
                    note: '',
                    description: '',
                }));
                const created = { ...await fetchById(request.data.id), kids: [] };
                context.commit('store', created);
                logActivity('CREATE', 'kidSchool', created.id, null, created);
                await context.dispatch('instructorTable/refreshDate', { date: moment(kidSchool.dateTime) }, { root: true });
            }
            catch (e) {
                console.log(e);
                throw e;
            }
        },
        async update(context, kidSchool) {
            try {
                const previousValue = context.state.find((k) => k.id === kidSchool.id);
                await kidSchoolApi.put(`/api/v1/internal/kid-schools/${kidSchool.id}`, toWritePayload(kidSchool));
                const updated = await fetchById(kidSchool.id);
                context.commit('update', updated);
                logActivity('UPDATE', 'kidSchool', kidSchool.id, previousValue ? previousValue : null, updated);
                await context.dispatch('instructorTable/refreshDate', { date: moment(updated.dateTime) }, { root: true });
            }
            catch (e) {
                console.log(e);
                throw e;
            }
        },
        async destroy(context, id) {
            const previousValue = context.state.find((k) => k.id === id);
            await kidSchoolApi.delete(`/api/v1/internal/kid-schools/${id}`);
            context.commit('destroy', id);
            logActivity('DELETE', 'kidSchool', id, previousValue ? previousValue : null, null);
        },
    },
    getters: {
        all(state) {
            return state;
        },
        find(state) {
            return (date) => {
                return state.filter(x => moment(x.dateTime).isSame(date, "date"));
            };
        },
    }
};
